<!-- MoreSheet.vue - the "Mehr" bottom sheet of the pill nav (Figma "Pill-Menü „Mehr“ · Varianten", variant A).
     Holds settings, the cities page and the legal pages, so app screens need no footer. Impressum and
     Barrierefreiheit stay two taps away from every screen (BGH I ZR 228/03; § 7 BITV 2.0).
     The sheet sits under the floating nav, so the nav's "Schließen" item stays visible above it. -->
<template>

    <transition name="more">
      <div v-if="open" class="more-layer">
        <div class="more-scrim" @click="$emit('close')"></div>
        <div ref="sheet" id="more-sheet" class="more-sheet" role="dialog" aria-modal="true" aria-labelledby="more-title" >
          <span class="grabber" aria-hidden="true"></span>
          <div class="more-head">
            <h2 id="more-title">{{ $t('common.moreSheet.title') }}</h2>
          </div>
          <nav :aria-label="$t('common.moreSheet.title')">
            <ul>
              <li v-for="it in visibleItems" :key="it.to" :class="{ gap: it.to === '/accessibility' }">
                <router-link :to="$lp(it.to)" class="more-row" @click="$emit('close')">
                  <span class="more-ic" aria-hidden="true"><i :class="it.icon"></i></span>
                  <span class="more-txt">
                    <span class="more-label">{{ $t(it.label) }}</span>
                    <span v-if="it.hint" class="more-hint">{{ $t(it.hint) }}</span>
                  </span>
                  <i class="fas fa-chevron-right more-chev" aria-hidden="true"></i>
                </router-link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </transition>

</template>

<script>
export default {
  name: 'MoreSheet',
  props: { open: { type: Boolean, default: false } },
  emits: ['close'],
  data() {
    return {
      isStaff: false,
      items: [
        { to: '/profile', icon: 'fas fa-user', label: 'common.nav.profile', hint: 'common.moreSheet.profileHint' },
        { to: '/settings', icon: 'fas fa-gear', label: 'common.nav.settings', hint: 'common.moreSheet.settingsHint' },
        { to: '/kommunen', icon: 'fas fa-city', label: 'common.footer.cities', hint: 'common.moreSheet.citiesHint' },
        { to: '/accessibility', icon: 'fas fa-universal-access', label: 'common.footer.accessibility' },
        { to: '/impressum', icon: 'fas fa-file-lines', label: 'legal.imprint.title' },
        { to: '/datenschutz', icon: 'fas fa-lock', label: 'legal.privacy.title' },
      ],
    };
  },
  computed: {
    // Staff of a city (users.municipality set) also get their dashboard; checked when the sheet opens
    visibleItems() {
      if (!this.isStaff) return this.items;
      const city = { to: '/city', icon: 'fas fa-building-columns', label: 'common.moreSheet.city', hint: 'common.moreSheet.cityHint' };
      return [this.items[0], city, ...this.items.slice(1)];
    },
  },
  watch: {
    open(v) {
      if (v) {
        const m = this.$pb && this.$pb.authStore && this.$pb.authStore.isValid ? this.$pb.authStore.model : null;
        this.isStaff = !!(m && m.municipality);
      }
      document.documentElement.classList.toggle('more-open', v);
      if (v) this.$nextTick(() => { const a = this.$refs.sheet && this.$refs.sheet.querySelector('.more-row'); if (a) a.focus(); });
    },
  },
  beforeUnmount() { document.documentElement.classList.remove('more-open'); },
  methods: {
    // keep Tab inside the dialog plus the nav's "Schließen" button, which closes it
    trap(e) {
      const close = document.querySelector('.bottom-nav .more-item');
      const f = [...this.$refs.sheet.querySelectorAll('a[href], button'), ...(close ? [close] : [])];
      if (!f.length) return;
      e.preventDefault();
      const i = f.indexOf(document.activeElement);
      const n = e.shiftKey ? (i <= 0 ? f.length - 1 : i - 1) : (i === -1 || i === f.length - 1 ? 0 : i + 1);
      f[n].focus();
    },
  },
};
</script>

<style scoped>
.more-layer { position: fixed; inset: 0; z-index: 90; } /* nav is z 100, so it stays above */
.more-scrim { position: absolute; inset: 0; background: rgba(11, 36, 32, 0.32); }
.more-sheet {
  position: absolute; left: 50%; bottom: 0; transform: translateX(-50%);
  width: min(100%, 520px); box-sizing: border-box; max-height: calc(100dvh - 48px); overflow-y: auto;
  background: var(--color-surface); color: var(--color-text);
  border-radius: 28px 28px 0 0; box-shadow: var(--shadow-floating);
  padding: 10px 20px calc(112px + env(safe-area-inset-bottom, 0px));
}
.grabber { display: block; width: 40px; height: 4px; border-radius: 2px; background: var(--color-border); margin: 0 auto 8px; }
.more-head { text-align: left; display: flex; align-items: center; justify-content: space-between; padding: 4px 4px 8px; }
.more-head h2 { font-size: 20px; font-weight: 600; margin: 0; }
nav, ul, li { text-align: left; }
ul { list-style: none; margin: 0; padding: 0; display: block; }
li { display: block; width: 100%; }
li.gap { border-top: 1px solid var(--color-border); margin-top: 6px; padding-top: 6px; }
.more-row {
  display: flex; justify-content: flex-start; align-items: center; gap: 14px; width: 100%; box-sizing: border-box; text-align: left; min-height: var(--touch-min, 48px);
  padding: 6px 4px; border-radius: 16px; text-decoration: none; color: var(--color-text) !important;
}
.more-row:hover, .more-row:focus-visible { background: var(--color-subtle); }
.more-ic { flex: none; width: 44px; height: 44px; border-radius: 14px; display: grid; place-items: center; background: var(--color-band, #f3faf8); font-size: 17px; }
.more-txt { flex: 1; display: flex; flex-direction: column; align-items: flex-start; min-width: 0; text-align: left; }
.more-label { font-size: 15px; font-weight: 500; }
.more-hint { font-size: 12px; color: var(--color-secondary); }
.more-chev { font-size: 12px; color: var(--color-secondary); }

.more-enter-active, .more-leave-active { transition: opacity 0.22s ease; }
.more-enter-active .more-sheet, .more-leave-active .more-sheet { transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1); }
.more-enter-from, .more-leave-to { opacity: 0; }
.more-enter-from .more-sheet, .more-leave-to .more-sheet { transform: translate(-50%, 100%); }
@media (prefers-reduced-motion: reduce) {
  .more-enter-active, .more-leave-active, .more-enter-active .more-sheet, .more-leave-active .more-sheet { transition: none; }
}
</style>

<style>
html.more-open { overflow: hidden; }
</style>
